import React from 'react';
import { Quote, Check, Layers, Music, Mic2 } from 'lucide-react';
import { Section, Eyebrow, H2, Reveal, Ref, FONT, MONO } from './ui';

const ICONS = [Layers, Music, Mic2];

export const PromiseSection = ({ d, L, t }) => (
  <Section id="promise">
    <div className="grid lg:grid-cols-[1.2fr_1fr] gap-14 items-start">
      <Reveal>
        <Eyebrow>{t.promise}</Eyebrow>
        <div className="mt-5 relative">
          <Quote className="absolute -top-3 -left-2 w-16 h-16 text-blue-100" />
          <p className="relative text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold leading-[1.15] tracking-tight text-slate-900" style={FONT} data-testid="promise-text">{L(d.promise)}</p>
        </div>
      </Reveal>
      <Reveal delay={150}>
        <div className="rounded-3xl bg-slate-50 border border-slate-200 p-7 space-y-4" data-testid="seminar-contents">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold" style={MONO}>{t.contents}</p>
          {L(d.contents).map((c) => (
            <div key={c} className="flex gap-3"><span className="mt-0.5 w-6 h-6 shrink-0 rounded-full bg-blue-600 flex items-center justify-center"><Check className="w-3.5 h-3.5 text-white" /></span><p className="text-slate-700">{c}</p></div>
          ))}
          <p className="text-xs text-slate-500 pt-3 border-t border-slate-200" data-testid="credits-note">{L(d.meta.credits)}</p>
        </div>
      </Reveal>
    </div>
  </Section>
);

export const Why = ({ d, L }) => (
  <Section id="why" dark>
    <div className="grid lg:grid-cols-2 gap-14 items-center">
      <Reveal>
        <Eyebrow dark>{L(d.why.eyebrow)}</Eyebrow>
        <H2 dark className="mt-4">{L(d.why.title)}</H2>
        <p className="mt-6 text-lg text-blue-100/80">{L(d.why.body)}</p>
        <p className="mt-6 text-xl font-bold text-white">{L(d.why.punch)}</p>
        <div className="mt-6"><Ref dark>{d.why.ref}</Ref></div>
      </Reveal>
      <Reveal delay={150}>
        <div className="relative aspect-square max-w-md mx-auto flex items-center justify-center" data-testid="why-hello">
          {[1, 0.78, 0.56].map((s, i) => <span key={s} className="absolute rounded-full border border-blue-400/30" style={{ inset: `${(1 - s) * 50}%`, animation: `vfPulse 3s ease-out ${i * 0.6}s infinite` }} />)}
          <p className="text-6xl sm:text-7xl font-extrabold tracking-tight bg-gradient-to-br from-white to-blue-300 bg-clip-text text-transparent" style={FONT}>“{d.why.quote.replace('.', '')}.”</p>
        </div>
      </Reveal>
    </div>
  </Section>
);

export const LayersBlock = ({ d, L }) => (
  <Section id="framework">
    <Reveal><Eyebrow>{L(d.layers.eyebrow)}</Eyebrow><H2 className="mt-4 max-w-3xl">{L(d.layers.title)}</H2></Reveal>
    <div className="mt-12 grid md:grid-cols-3 gap-5" data-testid="framework-layers">
      {d.layers.items.map((x, i) => {
        const I = ICONS[i];
        return (
          <Reveal key={i} delay={i * 120}>
            <div className="group h-full rounded-3xl border border-slate-200 p-8 bg-gradient-to-b from-white to-slate-50 hover:-translate-y-1 hover:border-blue-400 hover:shadow-xl hover:shadow-blue-500/10 transition-[transform,box-shadow,border-color] duration-300" data-testid={`framework-layer-${i}`}>
              <div className="flex items-center justify-between">
                <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-md"><I className="w-6 h-6 text-white" /></span>
                <span className="text-5xl font-extrabold text-slate-100 group-hover:text-blue-100 transition-colors" style={FONT}>0{i + 1}</span>
              </div>
              <p className="mt-6 text-[11px] uppercase tracking-[0.2em] text-blue-600 font-semibold" style={MONO}>{L(x.key)}</p>
              <p className="mt-1 text-2xl font-extrabold" style={FONT}>{L(x.name)}</p>
              <p className="mt-3 text-slate-600">{L(x.body)}</p>
            </div>
          </Reveal>
        );
      })}
    </div>
    <Reveal><p className="mt-8 text-slate-600 max-w-3xl border-l-4 border-blue-600 pl-5">{L(d.layers.note)}</p></Reveal>
  </Section>
);
